import { useEffect, useRef, useState } from 'react'
import { ClientApi } from '../../../services/clientApi'
import { NuiMessages } from '../../../utils/ClientEvents'
import './Speedometer.css'

interface SpeedometerWindow extends Window {
	speedometer?: {
		setSpeed: (metersPerSecond: number) => void
		setRPM: (value: number) => void
		setGear: (value: number) => void
		getValues: () => { speed: number, rpm: number, gear: number, mode: string }
	}
}

declare global {
	interface Window {
		testSpeedometer?: (speedMps: number, rpm: number, gear: number) => void
		testSpeedometerLoop?: () => void
	}
}

export const Speedometer = () => {
	const [isVisible, setIsVisible] = useState(false)
	const iframeRef = useRef<HTMLIFrameElement>(null)
	const isLoadedRef = useRef(false)
	const pendingUpdateRef = useRef<{ speed?: number, rpm?: number, gear?: number } | null>(null)

	const applyUpdate = (data: { speed?: number, rpm?: number, gear?: number }) => {
		const speedometerWindow = iframeRef.current?.contentWindow as SpeedometerWindow | null | undefined
		const speedometer = speedometerWindow?.speedometer

		if (!isLoadedRef.current || !speedometer) {
			pendingUpdateRef.current = { ...pendingUpdateRef.current, ...data }
			return
		}

		if (data.speed !== undefined) speedometer.setSpeed(data.speed)
		if (data.rpm !== undefined) speedometer.setRPM(data.rpm)
		if (data.gear !== undefined) speedometer.setGear(data.gear)
	}

	const handleIframeLoad = () => {
		isLoadedRef.current = true
		if (pendingUpdateRef.current) {
			applyUpdate(pendingUpdateRef.current)
			pendingUpdateRef.current = null
		}
	}

	useEffect(() => {
		ClientApi.on(NuiMessages.SHOW_SPEEDOMETER, () => {
			setIsVisible(true)
		})

		ClientApi.on(NuiMessages.HIDE_SPEEDOMETER, () => {
			setIsVisible(false)
		})

		ClientApi.on(NuiMessages.SPEEDOMETER_UPDATE, (data?: { speed?: number, rpm?: number, gear?: number }) => {
			if (data) applyUpdate(data)
		})

		// Test command available in devtools console: window.testSpeedometer(speedMps, rpm, gear)
		window.testSpeedometer = (speedMps: number, rpm: number, gear: number) => {
			setIsVisible(true)
			applyUpdate({ speed: speedMps, rpm, gear })
		}
	}, [])

	return (
		<div className="speedometer-root" style={{ display: isVisible ? 'flex' : 'none' }}>
			<iframe
				ref={iframeRef}
				onLoad={handleIframeLoad}
				src="/standalone.html"
				title="Speedometer"
				className="speedometer-iframe"
			/>
		</div>
	)
}
